import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import { readableOn } from "./badgeColors";

/* "#e8f5e9" -> [232, 245, 233]. jsPDF takes channels, the badge palette is
   written in hex, and this is the only place the two meet. */
const hexToRgb = (hex) => {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(String(hex || ""));
  return m
    ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)]
    : null;
};

export const exportToPDF = (
  data,
  columns,
  title = "Report",
  dateRange = null,
  footerMap = null,
  customWidths = null,
  appliedFilters = [], // <--- NEW PARAMETER
  /* { [columnKey]: (value, row) => ({ bg, text }) } — the same value-based
     colours the on-screen report uses, so an exported PDF and the report it
     was exported from do not disagree about which rows are bugs. Optional:
     a caller that passes nothing gets the plain table it always got. */
  cellStyles = null
) => {
  if (!data || data.length === 0) {
    alert("No data available to export.");
    return;
  }

  // 1. Initialize PDF (Landscape, A4)
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  // 2. Filter Columns (Hide actions or hidden cols)
  const printColumns = columns.filter(
    (col) => col.key !== "actions" && !col.hidden
  );

  // 3. Prepare Headers
  const headers = [printColumns.map((col) => col.label)];

  // 4. Calculate Column Widths
  const pageWidth = doc.internal.pageSize.width;
  const marginX = 10;
  const availableWidth = pageWidth - marginX * 2;

  let totalPixelWidth = 0;
  printColumns.forEach((col) => {
    let w = 100;
    if (customWidths && customWidths[col.key]) {
      w = customWidths[col.key];
    } else if (col.width) {
      w = parseInt(String(col.width).replace("px", ""), 10);
    }
    totalPixelWidth += w;
  });

  const dynamicColumnStyles = {};
  printColumns.forEach((col, index) => {
    let rawWidth = 100;
    if (customWidths && customWidths[col.key]) {
      rawWidth = customWidths[col.key];
    } else if (col.width) {
      rawWidth = parseInt(String(col.width).replace("px", ""), 10);
    }
    const ratio = rawWidth / totalPixelWidth;
    const pdfColWidth = ratio * availableWidth;

    dynamicColumnStyles[index] = {
      cellWidth: pdfColWidth,
      halign: "left",
      valign: "middle",
    };
    if (index === 0) {
      dynamicColumnStyles[index].halign = "center";
      dynamicColumnStyles[index].fontStyle = "bold";
    }
  });

  // 5. Prepare Body Data
  const body = data.map((row) => {
    return printColumns.map((col) => {
      if (col.exportFormatter) {
        return col.exportFormatter(row);
      }
      let val = row[col.key];
      if (val) {
        if (col.type === "date") {
          try {
            val = format(new Date(val), "dd-MMM-yyyy");
          } catch (e) {}
        } else if (col.type === "datetime") {
          try {
            val = format(new Date(val), "dd-MMM-yyyy HH:mm");
          } catch (e) {}
        }
      }
      if (typeof val === "boolean") return val ? "Yes" : "No";
      return val !== null && val !== undefined ? String(val) : "-";
    });
  });

  // 6. Prepare Footer Row
  let footerRow = null;
  if (footerMap) {
    footerRow = printColumns.map((col) => footerMap[col.key] || "");
  }

  // 7. Dynamic Font Sizing
  const colCount = printColumns.length;
  let fontSize = 8;
  let cellPadding = 2;
  if (colCount > 12) {
    fontSize = 7;
    cellPadding = 1.5;
  }

  /* ------------------------------------------------------------------ */
  /* 8. Letterhead                                                       */
  /* ------------------------------------------------------------------ */
  /* Page one gets the full masthead; every page after it gets a one-line
     running head, so a sheet pulled out of the middle of a fifteen-page
     report still says what it is and when it was run. The old version drew
     the masthead once and left pages 2..n starting with a bare table. */
  const companyName = "SKY RP SYSTEM";
  const reportTitle = title.toUpperCase();
  const generatedDate = format(new Date(), "dd MMM yyyy, hh:mm a");

  const BRAND = [29, 78, 216];   // deep blue — the masthead and table head
  const INK = [30, 41, 59];      // near-black for values
  const MUTED = [100, 116, 139]; // labels and meta
  const RULE = [226, 232, 240];  // hairlines

  const rangeStr =
    dateRange && dateRange.startDate
      ? `${format(dateRange.startDate, "dd MMM yyyy")} - ${format(
          dateRange.endDate,
          "dd MMM yyyy"
        )}`
      : null;

  /* Meta as label/value pairs rather than "Label: value" in one string. The
     labels line up in a column of their own, which is what stops the block
     reading as three unrelated sentences. */
  const meta = [
    ["Generated", generatedDate],
    ["Records", String(data.length)],
    ...(rangeStr ? [["Date Range", rangeStr]] : []),
  ];

  const drawMasthead = () => {
    // Accent bar — the one piece of solid colour, so the eye starts here.
    doc.setFillColor(...BRAND);
    doc.rect(marginX, 8, 1.6, 12, "F");

    doc.setFontSize(15);
    doc.setTextColor(...BRAND);
    doc.setFont("helvetica", "bold");
    doc.text(companyName, marginX + 4, 14);

    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.text(reportTitle, marginX + 4, 19.5);

    // Meta, right-aligned, labels in grey and values in ink.
    let y = 11;
    doc.setFontSize(7.5);
    meta.forEach(([label, value]) => {
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...MUTED);
      doc.text(`${label}`, pageWidth - marginX - 42, y, { align: "right" });
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...INK);
      doc.text(value, pageWidth - marginX, y, { align: "right" });
      y += 4;
    });

    return Math.max(23, y);
  };

  const drawRunningHead = () => {
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...BRAND);
    doc.text(companyName, marginX, 10);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(...MUTED);
    doc.text(reportTitle, marginX + doc.getTextWidth(companyName) + 3, 10);

    doc.text(generatedDate, pageWidth - marginX, 10, { align: "right" });

    doc.setDrawColor(...RULE);
    doc.setLineWidth(0.2);
    doc.line(marginX, 12, pageWidth - marginX, 12);
    return 15;
  };

  let headerY = drawMasthead();

  /* What the reader is looking at, if it is not everything. Printed as chips
     under the masthead rather than right-aligned into the meta block, where
     they used to collide with it and get truncated at 50 characters. */
  if (appliedFilters && appliedFilters.length > 0) {
    headerY += 1.5;
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...MUTED);
    doc.text("FILTERED BY", marginX, headerY);

    let x = marginX + doc.getTextWidth("FILTERED BY") + 3;
    doc.setFont("helvetica", "normal");

    appliedFilters.forEach((filterText) => {
      const w = doc.getTextWidth(filterText) + 4;
      // Wrap onto the next line rather than running off the sheet.
      if (x + w > pageWidth - marginX) {
        x = marginX;
        headerY += 5;
      }
      doc.setFillColor(241, 245, 249);
      doc.roundedRect(x, headerY - 3.2, w, 4.6, 1, 1, "F");
      doc.setTextColor(...INK);
      doc.text(filterText, x + 2, headerY);
      x += w + 2;
    });
    headerY += 2;
  }

  // Hairline between the letterhead and the table.
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.3);
  doc.line(marginX, headerY + 2, pageWidth - marginX, headerY + 2);

  const tableStartY = headerY + 5;

  // 9. Generate Table
  autoTable(doc, {
    head: headers,
    body: body,
    foot: footerRow ? [footerRow] : null,
    startY: tableStartY, // first page only; margin.top covers the rest
    theme: "grid",
    showHead: "everyPage",
    showFoot: "lastPage",
    /* Room at the top of pages 2..n for the running head, and at the bottom
       of every page for the footer rule. Without the bottom margin the last
       row of a full page sits on top of the page number. */
    margin: { left: marginX, right: marginX, top: 18, bottom: 14 },
    styles: {
      fontSize: fontSize,
      cellPadding: { top: cellPadding + 0.6, right: cellPadding, bottom: cellPadding + 0.6, left: cellPadding },
      font: "helvetica",
      textColor: INK,
      /* Hairlines in a pale grey rather than the default near-black grid.
         With eighteen columns the old borders carried more visual weight
         than the values inside them. */
      lineWidth: 0.1,
      lineColor: RULE,
      overflow: "linebreak",
      valign: "middle",
      halign: "left",
    },
    headStyles: {
      fillColor: BRAND,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
      lineWidth: 0,
      cellPadding: { top: 2.4, right: cellPadding, bottom: 2.4, left: cellPadding },
    },
    alternateRowStyles: { fillColor: [247, 250, 252] },
    footStyles: {
      fillColor: [237, 242, 247],
      textColor: INK,
      fontStyle: "bold",
      halign: "left",
      lineWidth: 0.1,
      lineColor: RULE,
    },
    columnStyles: dynamicColumnStyles,
    /* Paint the value-based colours onto body cells. Head and foot keep their
       own styling — a coloured header would fight the blue band, and a total
       row has no single value to colour by. */
    didParseCell: function (hook) {
      if (!cellStyles || hook.section !== "body") return;

      const col = printColumns[hook.column.index];
      const fn = col && cellStyles[col.key];
      if (!fn) return;

      const raw = Array.isArray(hook.cell.raw)
        ? hook.cell.raw.join(" ")
        : hook.cell.raw;
      if (raw === null || raw === undefined || raw === "" || raw === "-") return;

      // Same legibility floor the on-screen report applies.
      const colors = readableOn(fn(raw, data[hook.row.index]));
      if (!colors) return;

      const bg = hexToRgb(colors.bg);
      const text = hexToRgb(colors.text);
      if (bg) hook.cell.styles.fillColor = bg;
      if (text) hook.cell.styles.textColor = text;
      if (bg || text) hook.cell.styles.fontStyle = "bold";
    },
    /* Page one already has its masthead, drawn before the table. Every page
       after it gets the running head here — autoTable calls this once per
       page as it lays them out. */
    didDrawPage: function (hook) {
      if (hook.pageNumber > 1) drawRunningHead();
    },
  });

  /* ------------------------------------------------------------------ */
  /* 10. Footers                                                         */
  /* ------------------------------------------------------------------ */
  /* Stamped after the table rather than during it, because "Page 3 of 15"
     needs a total that does not exist until the last page has been laid
     out. The old footer said "Page 3" and left the reader to guess whether
     more was coming — on a fifteen-page report that matters. */
  const pageCount = doc.internal.getNumberOfPages();
  const footerY = doc.internal.pageSize.height - 7;

  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);

    doc.setDrawColor(...RULE);
    doc.setLineWidth(0.2);
    doc.line(marginX, footerY - 3, pageWidth - marginX, footerY - 3);

    doc.setFontSize(6.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...MUTED);
    doc.text(`${companyName}  -  ${reportTitle}`, marginX, footerY);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, footerY, {
      align: "right",
    });
  }

  // 11. Save File
  const fileName = `${title.replace(/\s+/g, "_")}_${format(
    new Date(),
    "yyyyMMdd"
  )}.pdf`;
  doc.save(fileName);
};